import {
  runRenameCanvasWorkflow,
  runRenameChatWorkflow,
} from "@/action-dialog";
import { orgAPI } from "@/api/api";
import {
  restoreErrorMessage,
  useRestoreCanvas,
  useRestoreFlowSession,
} from "@/api/archived";
import { useCanvasById, useCreateCanvas, useDeleteCanvas } from "@/api/canvas";
import { useDeleteFlowSession, useFlowSessionById } from "@/api/flow_sessions";
import { useCheckBulkPermissions } from "@/api/relations";
import { usePrivateWorkspace } from "@/api/workspaces";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { InternalFeedbackWidget } from "@/components/InternalFeedbackWidget";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROUTES } from "@/constants/routes";
import {
  useOpenTabs,
  type CanvasTab,
  type ChatTab,
  type OpenTab,
} from "@/context/OpenTabsContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useToggleTheme } from "@/hooks/useToggleTheme";
import { WorkspaceHeaderButton } from "@/layouts/WorkspaceLayout/components/WorkspaceHeaderButton";
import { WorkspaceMobileTabSwitcher } from "@/layouts/WorkspaceLayout/components/WorkspaceTabBar/WorkspaceMobileTabSwitcher";
import { WorkspaceTabContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceTabBar/WorkspaceTabContextMenu";
import { WorkspaceTabStrip } from "@/layouts/WorkspaceLayout/components/WorkspaceTabBar/WorkspaceTabStrip";
import { useCreatePrivateWorkspaceChat } from "@/pages/workspaces/hooks/useChatCardActions";
import { useEventStream } from "@/providers/EventStreamProvider";
import type { EventStreamEvent } from "@/types/events";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import type { Canvas, InboxItem } from "@cohesive/api-client";
import {
  FLOW_SESSION_MODES,
  getCanvasHeroImageUrl,
} from "@cohesive/api-client";
import { useThemeContext } from "@cohesive/ui-components";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  Frame,
  Home,
  MessageSquare,
  MessageSquarePlus,
  Moon,
  Plus,
  SquareDashedTopSolid,
  Sun,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useMatch, useNavigate } from "react-router-dom";

function tabRoute(tab: OpenTab): string {
  if (tab.kind === "chat") {
    return route(ROUTES.CHAT.DETAIL, { flowSessionId: tab.id });
  }
  return route(ROUTES.CANVAS.DETAIL, { canvasId: tab.id });
}

function openTabToCanvasStub(tab: CanvasTab): Canvas {
  const now = new Date().toISOString();
  return {
    id: tab.id,
    organization_id: "",
    user_id: "",
    liveblocks_room_id: "",
    name: tab.name,
    created_at: now,
    updated_at: now,
  };
}

function openTabToInboxItem(tab: ChatTab): InboxItem {
  return {
    id: tab.id,
    organization_id: "",
    user_id: "",
    flow_session_id: tab.id,
    resource_type: "flow_session",
    is_read: true,
    is_starred: false,
    is_archived: false,
    has_pending_tool_confirmations: false,
    created_at: "",
    updated_at: "",
    flow_session: {
      id: tab.id,
      mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
      name: tab.name,
      status: "ready",
      created_at: "",
      updated_at: "",
    },
  };
}

function CanvasTabSync() {
  const isCanvasDetail = useMatch(ROUTES.CANVAS.DETAIL);
  const activeCanvasId = isCanvasDetail?.params?.canvasId;
  const { data: activeCanvas } = useCanvasById(activeCanvasId, {
    enabled: !!activeCanvasId,
  });
  const { addTab } = useOpenTabs();
  const { mode } = useThemeContext();

  useEffect(() => {
    if (!activeCanvas?.id) return;
    if (activeCanvas.archived_at) return;
    addTab({
      kind: "canvas",
      id: activeCanvas.id,
      name: activeCanvas.name || "Untitled",
      imageUrl: getCanvasHeroImageUrl(activeCanvas, mode),
    });
  }, [
    activeCanvas?.id,
    activeCanvas?.name,
    activeCanvas?.hero_image_url,
    activeCanvas?.hero_image_light_url,
    activeCanvas?.hero_image_dark_url,
    activeCanvas?.archived_at,
    mode,
    addTab,
  ]);

  return null;
}

function ChatTabSync() {
  const isChatDetail = useMatch(ROUTES.CHAT.DETAIL);
  const activeSessionId = isChatDetail?.params?.flowSessionId;
  const { data: session } = useFlowSessionById(activeSessionId ?? "", {
    enabled: !!activeSessionId,
  });
  const { addTab } = useOpenTabs();

  useEffect(() => {
    if (!session?.id) return;
    if (session.archived_at) return;
    addTab({
      kind: "chat",
      id: session.id,
      name: session.name || "Untitled chat",
    });
  }, [session?.id, session?.name, session?.status, addTab]);

  return null;
}

function DeletedResourceTabSync() {
  const { registerHandler } = useEventStream();
  const { closeTab } = useOpenTabs();

  useEffect(() => {
    const handler = (event: EventStreamEvent) => {
      if (
        (event.type === "canvas.updated" && event.data.canvas?.archived_at) ||
        event.type === "canvas.deleted"
      ) {
        if (event.data.canvas?.id) closeTab(String(event.data.canvas.id));
      }
      if (
        (event.type === "flow_session.updated" &&
          event.data.flow_session?.archived_at) ||
        event.type === "flow_session.deleted"
      ) {
        if (event.data.flow_session?.id) {
          closeTab(String(event.data.flow_session.id));
        }
      }
    };
    return registerHandler(handler);
  }, [registerHandler, closeTab]);

  return null;
}

export function useWorkspaceTabBarState() {
  const navigate = useNavigate();
  const isCanvasDetail = useMatch(ROUTES.CANVAS.DETAIL);
  const isChatDetail = useMatch(ROUTES.CHAT.DETAIL);

  const activeId =
    isCanvasDetail?.params?.canvasId ??
    isChatDetail?.params?.flowSessionId ??
    null;

  const {
    openTabs,
    closeTab,
    closeOtherTabs,
    closeTabsToRight,
    reorderTabs,
    clearTabs,
  } = useOpenTabs();

  const closeTabAndNavigate = useCallback(
    (id: string) => {
      closeTab(id);
      if (id === activeId) {
        const idx = openTabs.findIndex((t) => t.id === id);
        const remaining = openTabs.filter((t) => t.id !== id);
        if (remaining.length > 0) {
          const nextIdx = Math.min(
            idx === -1 ? remaining.length - 1 : idx,
            remaining.length - 1,
          );
          navigate(tabRoute(remaining[nextIdx]));
        } else {
          navigate(ROUTES.CANVAS.ROOT);
        }
      }
    },
    [activeId, closeTab, navigate, openTabs],
  );

  return {
    activeId,
    openTabs,
    closeTab,
    closeOtherTabs,
    closeTabsToRight,
    reorderTabs,
    clearTabs,
    closeTabAndNavigate,
    navigate,
  };
}

function ThemeToggleHeaderButton() {
  const { mode } = useThemeContext();
  const { toggleTheme } = useToggleTheme();
  return (
    <WorkspaceHeaderButton
      icon={mode === "dark" ? Sun : Moon}
      label="Toggle theme"
      onClick={toggleTheme}
    />
  );
}

export function WorkspaceHeaderUtilities({
  onCloseTab,
}: {
  onCloseTab: (id: string) => void;
}) {
  const lg = useBreakpoint("lg");
  const navigate = useNavigate();
  const isHome = useMatch(ROUTES.HOME);
  const isCanvasDetail = useMatch(ROUTES.CANVAS.DETAIL);
  const isChatDetail = useMatch(ROUTES.CHAT.DETAIL);
  const activeId =
    isCanvasDetail?.params?.canvasId ??
    isChatDetail?.params?.flowSessionId ??
    null;
  const isHomeActive = !!isHome && !activeId;

  return (
    <>
      {!lg && (
        <>
          <WorkspaceHeaderButton
            icon={Home}
            label="Home"
            pressed={isHomeActive}
            onClick={() => navigate(ROUTES.CANVAS.ROOT)}
          />
          <WorkspaceMobileTabSwitcher onCloseTab={onCloseTab} />
        </>
      )}
      <ThemeToggleHeaderButton />
      <InternalFeedbackWidget
        side="bottom"
        align="end"
        sideOffset={20}
        alignOffset={-6}
        renderTrigger={({ toggle, isOpen }) => (
          <Tooltip>
            <TooltipTrigger asChild>
              <WorkspaceHeaderButton
                icon={MessageSquarePlus}
                label={isOpen ? "Close feedback" : "Send feedback"}
                pressed={isOpen}
                onClick={toggle}
              />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={6}>
              Send feedback
            </TooltipContent>
          </Tooltip>
        )}
      />
    </>
  );
}

export function WorkspaceDesktopTabBar() {
  const isHome = useMatch(ROUTES.HOME);
  const isCanvasRoot = useMatch(ROUTES.CANVAS.ROOT);
  const isWorkspaceRoot = useMatch(ROUTES.WORKSPACES.ROOT + "/*");
  const isSharedWithMe = useMatch(ROUTES.SHARED.ROOT);
  const isTrash = useMatch(ROUTES.ARCHIVE.ROOT);
  const isChatDetail = useMatch(ROUTES.CHAT.DETAIL);
  const isCanvasDetail = useMatch(ROUTES.CANVAS.DETAIL);

  const showTabBar =
    !!isHome ||
    !!isWorkspaceRoot ||
    !!isCanvasRoot ||
    !!isSharedWithMe ||
    !!isTrash ||
    !!isChatDetail ||
    !!isCanvasDetail;

  const {
    activeId,
    openTabs,
    closeOtherTabs,
    closeTabsToRight,
    reorderTabs,
    clearTabs,
    closeTabAndNavigate,
    navigate,
  } = useWorkspaceTabBarState();
  const { addTab } = useOpenTabs();

  const createCanvas = useCreateCanvas();
  const privateWorkspace = usePrivateWorkspace();
  const { create: createPrivateChat, isPending: isChatPending } =
    useCreatePrivateWorkspaceChat();

  const [draggingId, setDraggingId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggingId(event.active.id as string);
    setContextMenu(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const fromIdx = openTabs.findIndex((t) => t.id === active.id);
      const toIdx = openTabs.findIndex((t) => t.id === over.id);
      if (fromIdx !== -1 && toIdx !== -1) reorderTabs(fromIdx, toIdx);
    },
    [openTabs, reorderTabs],
  );

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    tabId: string;
  } | null>(null);
  const [suppressTooltipForTab, setSuppressTooltipForTab] = useState<
    string | null
  >(null);

  const closeContextMenu = useCallback(() => {
    const tid = contextMenu?.tabId ?? null;
    setContextMenu(null);
    if (tid) setSuppressTooltipForTab(tid);
  }, [contextMenu]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, tabId: string) => {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, tabId });
    },
    [],
  );

  const handleSelect = useCallback(
    (tab: OpenTab) => {
      if (tab.id === activeId) return;
      navigate(tabRoute(tab));
    },
    [activeId, navigate],
  );

  const handleNewCanvas = useCallback(() => {
    createCanvas.mutate(
      {},
      {
        onSuccess: (newCanvas) => {
          navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: newCanvas.id }));
        },
      },
    );
  }, [createCanvas, navigate]);

  const handleNewChat = useCallback(() => {
    if (privateWorkspace?.id) createPrivateChat(privateWorkspace.id);
  }, [createPrivateChat, privateWorkspace?.id]);

  const handleClose = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      closeTabAndNavigate(id);
    },
    [closeTabAndNavigate],
  );

  const handleContextMenuClose = useCallback(() => {
    if (!contextMenu) return;
    const { tabId } = contextMenu;
    closeContextMenu();
    closeTabAndNavigate(tabId);
  }, [contextMenu, closeContextMenu, closeTabAndNavigate]);

  const handleContextMenuCloseOthers = useCallback(() => {
    if (!contextMenu) return;
    const { tabId } = contextMenu;
    closeContextMenu();
    closeOtherTabs(tabId);
    if (tabId !== activeId) {
      const tab = openTabs.find((t) => t.id === tabId);
      if (tab) navigate(tabRoute(tab));
    }
  }, [
    contextMenu,
    closeContextMenu,
    closeOtherTabs,
    activeId,
    navigate,
    openTabs,
  ]);

  const handleContextMenuCloseRight = useCallback(() => {
    if (!contextMenu) return;
    const { tabId } = contextMenu;
    closeContextMenu();
    const idx = openTabs.findIndex((t) => t.id === tabId);
    if (idx === -1) return;
    const activeIdx = openTabs.findIndex((t) => t.id === activeId);
    closeTabsToRight(tabId);
    if (activeId && activeIdx > idx) {
      const tab = openTabs.find((t) => t.id === tabId);
      if (tab) navigate(tabRoute(tab));
    }
  }, [
    contextMenu,
    closeContextMenu,
    closeTabsToRight,
    openTabs,
    activeId,
    navigate,
  ]);

  const hasOtherTabs = openTabs.length > 1;
  const hasTabsToRight = useMemo(() => {
    if (!contextMenu) return false;
    const idx = openTabs.findIndex((t) => t.id === contextMenu.tabId);
    return idx !== -1 && idx < openTabs.length - 1;
  }, [contextMenu, openTabs]);

  const handleContextMenuCloseAll = useCallback(() => {
    closeContextMenu();
    clearTabs();
    navigate(ROUTES.CANVAS.ROOT);
  }, [closeContextMenu, clearTabs, navigate]);

  const handleContextMenuRename = useCallback(() => {
    if (!contextMenu) return;
    const tab = openTabs.find((t) => t.id === contextMenu.tabId);
    closeContextMenu();
    if (!tab) return;
    if (tab.kind === "canvas") {
      window.setTimeout(async () => {
        let canvas: Canvas = openTabToCanvasStub(tab);
        try {
          canvas = await orgAPI.canvas.getById(tab.id);
        } catch {
          toast.error("Failed to load canvas");
          return;
        }
        void runRenameCanvasWorkflow({
          canvas,
          onRenamed: (name) =>
            addTab({
              kind: "canvas",
              id: tab.id,
              name: name.trim() || "Untitled",
            }),
        });
      }, 0);
      return;
    }
    window.setTimeout(() => {
      void runRenameChatWorkflow({
        item: openTabToInboxItem(tab),
        onRenamed: (name) =>
          addTab({
            kind: "chat",
            id: tab.id,
            name: name.trim() || "Untitled chat",
          }),
      });
    }, 0);
  }, [contextMenu, openTabs, closeContextMenu, addTab]);

  const deleteCanvas = useDeleteCanvas({
    onError: () => toast.error("Failed to delete Canvas"),
  });
  const deleteFlowSession = useDeleteFlowSession({
    onError: () => toast.error("Failed to delete chat"),
  });
  const restoreCanvas = useRestoreCanvas({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Canvas")),
  });
  const restoreFlowSession = useRestoreFlowSession({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore chat")),
  });

  const handleContextMenuDelete = useCallback(() => {
    if (!contextMenu) return;
    const tab = openTabs.find((t) => t.id === contextMenu.tabId);
    closeContextMenu();
    if (!tab) return;
    const onSuccess = () => {
      closeTabAndNavigate(tab.id);
      showMovedToTrashToast(() => {
        if (tab.kind === "canvas") restoreCanvas.mutate(tab.id);
        else restoreFlowSession.mutate(tab.id);
      });
    };
    if (tab.kind === "canvas") {
      deleteCanvas.mutate(tab.id, { onSuccess });
    } else {
      deleteFlowSession.mutate(tab.id, { onSuccess });
    }
  }, [
    contextMenu,
    openTabs,
    closeContextMenu,
    closeTabAndNavigate,
    deleteCanvas,
    deleteFlowSession,
    restoreCanvas,
    restoreFlowSession,
  ]);

  const contextMenuTab = contextMenu
    ? openTabs.find((t) => t.id === contextMenu.tabId)
    : undefined;

  const { data: tabDeletePermission } = useCheckBulkPermissions({
    checks:
      contextMenuTab?.kind === "canvas"
        ? [
            {
              resource_object_type: "canvas" as const,
              resource_object_id: contextMenuTab.id,
              permission: "can_delete" as const,
            },
          ]
        : [],
  });
  const canDeleteContextMenuTab =
    contextMenuTab?.kind === "chat" ||
    (contextMenuTab?.kind === "canvas" &&
      (tabDeletePermission?.canvas?.[contextMenuTab.id]?.can_delete ?? false));

  if (!showTabBar) {
    return null;
  }

  const isHomeActive = !!isHome && !activeId;

  return (
    <>
      <WorkspaceHeaderButton
        icon={Home}
        label="Home"
        pressed={isHomeActive}
        onClick={() => navigate(ROUTES.CANVAS.ROOT)}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <WorkspaceTabStrip
          tabs={openTabs}
          activeId={activeId}
          suppressTooltipForTab={suppressTooltipForTab}
          draggingId={draggingId}
          contextMenuTabId={contextMenu?.tabId ?? null}
          onSelect={handleSelect}
          onClose={handleClose}
          onContextMenu={handleContextMenu}
          onMouseLeaveTab={(tabId) => {
            if (suppressTooltipForTab === tabId) setSuppressTooltipForTab(null);
          }}
          newTabMenu={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <WorkspaceHeaderButton
                  icon={Plus}
                  label="New tab"
                  className={cn(
                    createCanvas.isPending && "pointer-events-none opacity-50",
                  )}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={6}
                className="w-48"
              >
                <DropdownMenuItem
                  onClick={() => window.setTimeout(handleNewCanvas, 0)}
                  disabled={createCanvas.isPending}
                >
                  <SquareDashedTopSolid />
                  New Canvas
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.setTimeout(handleNewChat, 0)}
                  disabled={isChatPending || !privateWorkspace}
                >
                  <MessageSquare />
                  New Chat
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />

        <DragOverlay dropAnimation={null}>
          {draggingId &&
            (() => {
              const tab = openTabs.find((t) => t.id === draggingId);
              if (!tab) return null;
              const imageUrl = tab.kind === "canvas" ? tab.imageUrl : undefined;
              return (
                <div className="inline-flex h-8 w-44 shrink-0 cursor-grabbing select-none items-center gap-2 rounded-md border border-n-10 bg-n-00 px-2 text-base font-medium text-n-90 shadow-elev-3">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      className="size-[13px] shrink-0 rounded-[3.5px] object-cover"
                    />
                  ) : tab.kind === "chat" ? (
                    <MessageSquare className="size-3.5 shrink-0 text-n-40" />
                  ) : (
                    <Frame className="size-3.5 shrink-0 text-n-40" />
                  )}
                  <span className="truncate">{tab.name}</span>
                </div>
              );
            })()}
        </DragOverlay>
      </DndContext>

      {contextMenu && contextMenuTab && (
        <WorkspaceTabContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          hasOtherTabs={hasOtherTabs}
          hasTabsToRight={hasTabsToRight}
          onClose={closeContextMenu}
          onRename={handleContextMenuRename}
          onCloseTab={handleContextMenuClose}
          onCloseOthers={handleContextMenuCloseOthers}
          onCloseRight={handleContextMenuCloseRight}
          onCloseAll={handleContextMenuCloseAll}
          onDelete={
            canDeleteContextMenuTab ? handleContextMenuDelete : undefined
          }
        />
      )}
    </>
  );
}

/** @deprecated Use WorkspaceDesktopTabBar + WorkspaceHeaderUtilities via WorkspaceAppHeader */
export function WorkspaceTabBar() {
  const lg = useBreakpoint("lg");

  if (!lg) return null;

  return <WorkspaceDesktopTabBar />;
}

export function WorkspaceTabBarSync() {
  return (
    <>
      <CanvasTabSync />
      <ChatTabSync />
      <DeletedResourceTabSync />
    </>
  );
}
